/**
 * AdminBackup — one-click MongoDB backup export.
 *
 * Shows a summary of collections + doc counts, then a big Download button
 * that streams a ZIP archive from `GET /api/admin/backup/export`.
 */
import { useEffect, useState } from "react";
import { Download, RefreshCw, Loader2, ShieldCheck, Info } from "lucide-react";
import { http, API } from "@/lib/api";
import { toast } from "sonner";

export default function AdminBackup() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/backup/summary");
      setSummary(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load backup summary");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const download = async () => {
    setDownloading(true);
    try {
      // Trigger a native browser download so large ZIPs stream to disk
      // without going through the axios / JSON path.
      window.location.href = `${API}/admin/backup/export`;
      // Give the browser a beat to actually start the download before
      // resetting the button.
      setTimeout(() => setDownloading(false), 2500);
    } catch (e) {
      toast.error("Download failed");
      setDownloading(false);
    }
  };

  return (
    <div data-testid="admin-backup" className="max-w-4xl">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="font-display text-2xl mb-1">Data backup</h2>
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Download a complete snapshot of your Social Pulse database as a ZIP of JSONL files.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="wofy-btn-ghost text-sm"
          data-testid="admin-backup-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {/* Safety notice */}
      <div
        className="rounded-xl border p-4 flex items-start gap-3 mb-6"
        style={{ borderColor: "#93c5fd", background: "#eff6ff", color: "#1e3a8a" }}
      >
        <Info className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-semibold mb-1">What's in the backup</div>
          <ul className="text-xs space-y-1 opacity-90 list-disc pl-4">
            <li>One <code>.jsonl</code> file per MongoDB collection (users, family_trees, memorials, marketplace_listings, etc.)</li>
            <li>A <code>_manifest.json</code> with counts, sizes, and restore instructions</li>
            <li>Everything is JSON — you can inspect, grep, or import back with <code>mongoimport</code></li>
            <li>Store the ZIP somewhere safe (Google Drive, Dropbox, iCloud). Repeat weekly for peace of mind.</li>
          </ul>
        </div>
      </div>

      {/* Summary table */}
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--wofy-border)" }}>
        <div
          className="grid grid-cols-[1fr_auto] px-5 py-3 text-xs font-mono uppercase tracking-widest"
          style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}
        >
          <div>Collection</div>
          <div>Documents</div>
        </div>
        {loading && (
          <div className="px-5 py-6 text-sm inline-flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Counting…
          </div>
        )}
        {!loading && summary?.collections?.length > 0 && (
          <div className="divide-y" style={{ borderColor: "var(--wofy-border)" }}>
            {summary.collections.map((c) => (
              <div
                key={c.collection}
                className="grid grid-cols-[1fr_auto] px-5 py-2.5 text-sm"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`admin-backup-col-${c.collection}`}
              >
                <div className="font-mono">{c.collection}</div>
                <div className="tabular-nums" style={{ color: "var(--wofy-text-soft)" }}>{c.count ?? "?"}</div>
              </div>
            ))}
          </div>
        )}
        {!loading && summary && (
          <div
            className="grid grid-cols-[1fr_auto] px-5 py-3 text-sm font-semibold border-t"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
            data-testid="admin-backup-total"
          >
            <div>Total documents</div>
            <div className="tabular-nums">{summary.total_documents}</div>
          </div>
        )}
      </div>

      {/* Download button */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button
          onClick={download}
          disabled={downloading || loading}
          className="wofy-btn-primary"
          data-testid="admin-backup-download"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {downloading ? "Preparing your backup…" : "Download full backup (.zip)"}
        </button>
        <span className="text-xs inline-flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
          <ShieldCheck className="w-3.5 h-3.5" />
          Read-only — will not modify any data
        </span>
      </div>

      <div className="mt-6 text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
        Tip: run this weekly. Set a Google Calendar reminder titled "Social Pulse backup".
      </div>
    </div>
  );
}
